<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { buildDemoScript, type DemoLab, type DemoStep } from '../../model/demo-script';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import LabSection from './LabSection.vue';

  const lab = useMatrixFillLabContext();
  const done = ref<Set<string>>(new Set());

  function openSection(testId: string, focus: string | null): void {
    const section = document.querySelector(`[data-testid="${testId}"]`);
    if (section instanceof HTMLDetailsElement) section.open = true;
    void nextTick(() => {
      ((focus ? section?.querySelector(focus) : null) ?? section)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function openReport(): void {
    openSection('lab-report', 'table');
  }

  function tryArchive(): void {
    lab.tryArchive();
    openSection('lab-archive', null);
  }

  const demoLab = computed<DemoLab>(() => ({
    fieldOptions: lab.fieldOptions.value,
    storedRules: lab.storedRules.value,
    storedPermissions: lab.storedPermissions.value,
    anchorFieldIds: lab.anchorFieldIds.value,
    positions: lab.subjects.positions.value,
    functionalActivities: lab.subjects.functionalActivities.value,
    resetLab: lab.resetLab,
    setValue: lab.setValue,
    locateField: lab.locateField,
    selectSubject: (positionIds, functionalActivityIds) => {
      lab.selectedPositionIds.value = positionIds;
      lab.selectedActivityIds.value = functionalActivityIds;
    },
    compiled: () => lab.compiled.value,
    sampleValue: lab.sampleValue,
    saveResponse: lab.saveResponse,
    tryArchive,
    openReport,
  }));

  const steps = computed<DemoStep[]>(() => buildDemoScript(demoLab.value));

  watch(() => lab.matrix.value?.id, () => {
    done.value = new Set();
  });

  function execute(step: DemoStep): void {
    step.run();
    done.value = step.id === 'reset' ? new Set([step.id]) : new Set([...done.value, step.id]);
  }

  function restart(): void {
    lab.resetLab();
    done.value = new Set();
  }
</script>

<template>
  <LabSection
    title="Roteiro"
    :badge="steps.length > 0 ? `${done.size}/${steps.length}` : null"
    open
    hint="Passos derivados das regras, permissões e âncoras salvas nesta matriz. Execute em ordem e narre o que muda na folha."
    data-testid="lab-demo-script"
  >
    <p
      v-if="steps.length === 0"
      class="text-[var(--color-gray-05)]"
    >
      Esta matriz não tem regras, permissões nem âncoras salvas, então não há roteiro. Na visão do desenvolvedor, crie um cenário em "Simular regra ou permissão nova".
    </p>

    <ol
      v-else
      class="flex flex-col gap-1.5"
    >
      <li
        v-for="(step, index) in steps"
        :key="step.id"
        class="flex items-start gap-2 rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-02)] p-2"
        :class="{ 'border-[var(--theme-secondary)]': done.has(step.id) }"
      >
        <span
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
          :class="done.has(step.id) ? 'bg-[var(--theme-secondary)] text-white' : 'bg-[var(--color-gray-09)] text-[var(--color-gray-06)]'"
        >
          {{ done.has(step.id) ? '✓' : index + 1 }}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block font-semibold">{{ step.title }}</span>
          <span class="block text-[10px] text-[var(--color-gray-05)]">{{ step.description }}</span>
        </span>
        <button
          type="button"
          class="shrink-0 rounded-md border border-[var(--theme-secondary)] px-2 py-1 font-medium text-[var(--theme-secondary)] hover:bg-[var(--theme-secondary)] hover:text-white"
          @click="execute(step)"
        >
          Executar
        </button>
      </li>
    </ol>

    <button
      v-if="steps.length > 0"
      type="button"
      class="self-start text-[var(--color-gray-05)] underline"
      @click="restart"
    >
      Reiniciar roteiro
    </button>
  </LabSection>
</template>
